
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Edit, 
  Plus, 
  Search, 
  Trash, 
  Loader2,
  AlertCircle,
  Calendar
} from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { FormDialog } from "@/components/admin/FormDialog";
import { NewsForm, NewsFormValues } from "@/components/admin/NewsForm";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NewsAdmin = () => {
  const [search, setSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<any>(null);
  const [deletingNewsId, setDeletingNewsId] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  
  const { 
    data: newsItems = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['news'],
    queryFn: api.news.getAll
  });
  
  const filteredNews = newsItems.filter((item: any) => {
    const searchTerm = search.toLowerCase();
    const title = item.title?.toLowerCase() || '';
    const content = item.content?.toLowerCase() || '';
    const status = item.status?.toLowerCase() || '';
    
    // Add published_date to the search if it exists
    let publishedDate = '';
    if (item.published_date) {
      try {
        publishedDate = format(new Date(item.published_date), 'PPP').toLowerCase();
      } catch (e) {
        // If date parsing fails, just use the raw value
        publishedDate = item.published_date.toString().toLowerCase();
      }
    }
    
    return title.includes(searchTerm) || 
           content.includes(searchTerm) || 
           status.includes(searchTerm) ||
           publishedDate.includes(searchTerm);
  });

  const handleCreateNews = async (values: NewsFormValues) => {
    await api.news.create(values);
    setIsCreateDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['news'] });
  };

  const handleUpdateNews = async (values: NewsFormValues) => {
    if (editingNews) {
      await api.news.update(editingNews.id, values);
      setEditingNews(null);
      queryClient.invalidateQueries({ queryKey: ['news'] });
    }
  };

  const handleDeleteNews = async () => {
    if (deletingNewsId) {
      await api.news.delete(deletingNewsId);
      setDeletingNewsId(null);
      queryClient.invalidateQueries({ queryKey: ['news'] });
    }
  };

  // Handle empty form submissions
  const handleCreateSubmit = async () => {
    // This is just a placeholder to satisfy the FormDialog interface
    // The actual form submission is handled by NewsForm
  };

  const handleUpdateSubmit = async () => {
    // This is just a placeholder to satisfy the FormDialog interface
    // The actual form submission is handled by NewsForm
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">News</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add News Article
        </Button>
      </div>
      
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search news..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex justify-center items-center py-8 text-destructive">
          <AlertCircle className="h-8 w-8 mr-2" />
          <p>Error loading news. Please try again.</p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Published Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNews.length > 0 ? (
                filteredNews.map((item: any) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>
                      {item.published_date ? (
                        <div className="flex items-center">
                          <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                          {format(new Date(item.published_date), 'PPP')}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setEditingNews(item)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setDeletingNewsId(item.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No news found. Try adjusting your search or create a new news article.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      
      {/* Create News Dialog */}
      <FormDialog
        title="Create News Article"
        description="Add a new news article to your site"
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
      >
        <NewsForm onSubmit={handleCreateNews} />
      </FormDialog>
      
      {/* Edit News Dialog */}
      <FormDialog
        title="Edit News Article"
        description="Update news article information"
        isOpen={!!editingNews}
        onClose={() => setEditingNews(null)}
        onSubmit={handleUpdateSubmit}
      >
        {editingNews && (
          <NewsForm 
            defaultValues={editingNews} 
            onSubmit={handleUpdateNews} 
          />
        )}
      </FormDialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingNewsId} onOpenChange={() => setDeletingNewsId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the news article.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteNews}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewsAdmin;
